import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useOnboarding } from "../../../contexts/OnboardingContext";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const DiscoverySourceStep = () => {
  const { username, discoverySource, setDiscoverySource, nextStep } = useOnboarding();
  const [portalTarget, setPortalTarget] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      const el = document.getElementById("onboarding-continue-slot");
      if (el) setPortalTarget(el);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const sources = [
    { icon: "👨‍👩‍👧", label: "Friend / Family", value: "Friend/Family" },
    { icon: "/assets/google.png", label: "Google", value: "Google" },
    { icon: "/assets/tiktok.png", label: "TikTok", value: "TikTok" },
    { icon: "/assets/instagram.png", label: "Instagram", value: "Instagram" },
    { icon: "/assets/linkedin.png", label: "LinkedIn", value: "LinkedIn" },
    { icon: "🏫", label: "School", value: "School" },
    { icon: "/assets/whatsapp.png", label: "WhatsApp", value: "WhatsApp" },
    { icon: "✨", label: "Other", value: "Other" },
  ];

  const handleToggle = (value) => {
    if (discoverySource.includes(value)) setDiscoverySource(discoverySource.filter((s) => s !== value));
    else setDiscoverySource([...discoverySource, value]);
  };

  const continueButton = (
    <button
      onClick={nextStep}
      className="inline-flex items-center gap-2 transition active:scale-[0.97]"
      style={{
        ...FREDOKA,
        background: "#0065F8",
        color: "#FFFFFF",
        fontWeight: 700,
        fontSize: 15,
        border: "none",
        borderRadius: 999,
        padding: "12px 22px",
        boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
        cursor: "pointer",
      }}
    >
      Continue
      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col items-center px-4 animate-fadeIn">
      {/* Panda + Speech Bubble */}
      <div className="flex items-start gap-4 sm:gap-5 mb-10 w-full">
        <div className="flex-shrink-0">
          <div className="relative w-24 h-24 sm:w-32 sm:h-32">
            <div style={{ position: "absolute", inset: 0, background: "rgba(0,101,248,0.15)", filter: "blur(28px)", borderRadius: "50%", transform: "scale(1.4)" }} />
            <img
              src="/assets/pixley-panda-nobg.png"
              alt="Pixley Panda"
              className="relative w-full h-full object-contain z-10"
              onError={(e) => { e.target.src = "/assets/pixley-panda.png"; }}
              style={{ filter: "drop-shadow(0px 6px 12px rgba(11,26,51,0.18))" }}
            />
          </div>
        </div>
        <div className="flex-1 relative">
          <div
            className="absolute z-0"
            style={{
              left: -8, top: 28, width: 16, height: 16,
              background: "#FFFFFF",
              border: "2px solid rgba(11,26,51,0.06)",
              borderRight: "none", borderTop: "none",
              transform: "rotate(45deg)",
            }}
          />
          <div
            className="relative z-10 px-6 py-5 sm:px-7 sm:py-6"
            style={{
              background: "#FFFFFF",
              border: "2px solid rgba(11,26,51,0.06)",
              borderRadius: 22,
              boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 24px rgba(11,26,51,0.06)",
            }}
          >
            <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
              Welcome
            </span>
            <h1
              style={{
                ...FREDOKA,
                color: "#0B1A33",
                fontWeight: 700,
                fontSize: "clamp(1.3rem, 2.4vw, 1.7rem)",
                textTransform: "uppercase",
                letterSpacing: "-0.01em",
                lineHeight: 1.1,
                margin: "6px 0 6px",
              }}
            >
              So happy you're here{username ? `, ${username}` : ""}!
            </h1>
            <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: 0 }}>
              How did the magic of Pixley find you? Pick all that apply.
            </p>
          </div>
        </div>
      </div>

      {/* Sources Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 w-full mb-4">
        {sources.map((source) => {
          const isSelected = discoverySource.includes(source.value);
          const isImage = source.icon.startsWith("/");
          return (
            <button
              key={source.value}
              onClick={() => handleToggle(source.value)}
              className="group relative flex flex-col items-center justify-center gap-3 transition-all active:scale-[0.97]"
              style={{
                background: isSelected ? "linear-gradient(180deg, #EAF3FF 0%, #DBEAFE 100%)" : "#FFFFFF",
                border: isSelected ? "3px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
                borderRadius: 20,
                padding: "20px 12px",
                aspectRatio: "1",
                boxShadow: isSelected
                  ? "0px 5px 0px #073B8E, 0px 14px 28px rgba(0,101,248,0.20)"
                  : "0px 3px 0px rgba(11,26,51,0.06)",
                cursor: "pointer",
              }}
            >
              {isSelected && (
                <div
                  className="absolute"
                  style={{
                    top: 6, right: 6,
                    width: 22, height: 22,
                    background: "#0065F8",
                    color: "#FFFFFF",
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0px 2px 0px #073B8E",
                  }}
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
              <div>
                {isImage ? (
                  <img src={source.icon} alt={source.label} className="w-10 h-10 sm:w-12 sm:h-12 object-contain" />
                ) : (
                  <span style={{ fontSize: 38 }}>{source.icon}</span>
                )}
              </div>
              <span style={{ ...FREDOKA, color: isSelected ? "#0065F8" : "#0B1A33", fontWeight: 700, fontSize: 13, textAlign: "center" }}>
                {source.label}
              </span>
            </button>
          );
        })}
      </div>

      {discoverySource.length > 0 && (
        <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, fontWeight: 600, margin: 0 }}>
          <strong style={{ color: "#0065F8" }}>{discoverySource.length}</strong> source{discoverySource.length !== 1 ? "s" : ""} selected
        </p>
      )}

      {portalTarget && ReactDOM.createPortal(continueButton, portalTarget)}
    </div>
  );
};

export default DiscoverySourceStep;
